/* eslint-disable no-shadow */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { get as _get } from 'lodash';
import { api } from '../hooks/useAxios';
import { Invoice, SubscriptionStatus } from '../types/billing';

const { post, get } = api();

const precedingZero = (num) => (num < 10 ? `0${num}` : `${num}`);
const extractYear = (fourDigitYear) => fourDigitYear.toString().substr(-2);

const getInvoicesAPI = ():Promise<Invoice[]> => get({
  url: '/payments/invoices',
}, (d) => d?.invoices || []);
// const getInvoicesAPI = (): Promise<Invoice[]> => {
//   const freeTrial: Invoice = {
//     type: InvoiceType.FreeTrial,
//     details: {
//       periodStart: '2020-01-01',
//       periodEnd: '2020-02-01',
//     },
//   };
//   const paidPeriod: Invoice = {
//     type: InvoiceType.Paid,
//     details: {
//       id: '1',
//       periodStart: '2020-03-01',
//       periodEnd: '2020-04-01',
//       numberOfSeats: 20,
//       totalAmount: 2000,
//       pricing: InvoicePricing.Others,
//       downloadURL: 'https://download.com',
//       status: InvoiceStatus.Open,
//     },
//   };
//   const trialExpired: Invoice = {
//     type: InvoiceType.TrialExpired,
//     details: {
//       periodStart: '2020-01-01',
//       today: '2021-05-05',
//       numberOfSeats: 40,
//     },
//   };
//   return Promise.resolve([freeTrial, paidPeriod]);
// };

const getBillingDetailsAPI = (): Promise<{
  email: string,
  cardNumber: string,
  cardExpiry: string,
  pricing: 'INDIAN' | 'OTHERS',
  taxID: string | null,
  paymentMethodId: string | null,
} | null> => get({
  url: '/payments/billingDetails',
}, (res) => (res?.details ? ({
  pricing: res?.details?.pricing,
  email: res?.details?.customerEmail || '',
  cardNumber: res?.details?.cardDetails?.cardNumberLastFour,
  cardExpiry: `${precedingZero(res?.details?.cardDetails?.expiryMonth || 0)}/${extractYear(res?.details?.cardDetails?.expiryYear || '')}`,
  taxID: _get(res?.details, 'taxDetailsList.0.value', null),
  paymentMethodId: _get(res?.details, 'paymentMethod.id', null),
}) : null));

const getSessionID = ({ paymentMethodOnly = false }: {
  paymentMethodOnly: boolean,
}) => {
  const idExtract = (d) => {
    if (d?.sessionID) return d.sessionID;
    throw new Error('No session ID in response');
  };
  return post({
    url: '/payments/sessionID',
    data: {
      successURL: paymentMethodOnly ? `${window.location.origin}/settings/billing?paymentMethod=true` : `${window.location.origin}/settings/billing?paymentSuccess=true`,
      cancelURL: `${window.location.origin}/settings/billing/?cancelPayment=true`,
    },
  }, idExtract);
};

// eslint-disable-next-line consistent-return
const startSubscriptionAPI = async ({ paymentMethodOnly }) => {
  try {
    const Stripe = (window as any)?.Stripe;
    if (!Stripe) throw new Error('Could not initiate a subscription. Please reach out to us through the chat.');
    const sessionId = await getSessionID({
      paymentMethodOnly,
    });
    const s = await Stripe(process.env.NEXT_PUBLIC_STRIPE_CLIENT_ID as string);
    if (!s) throw new Error('Could not initiate a checkout.');
    s?.redirectToCheckout({
      sessionId,
    });
  } catch (e: any) {
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({
      response: {
        data: {
          message: e?.toString(),
        },
      },
    });
  }
};

const downloadInvoiceAPI = (id: string) => Promise.resolve(id);

const cancelSubscriptionAPI = () => Promise.resolve(true);

const updateBillingEmailAPI = (email:string) => Promise.resolve(email);

const openCustomerPortalAPI = async () => {
  const { customerPortalURL } = await post({
    url: '/payments/customerPortalID',
    data: {
      returnURL: `${window.location.origin}/settings/billing`,
    },
  });
  return window.location.assign(customerPortalURL);
};

const confirmPayment = async (args: {
  clientSecret: string,
  paymentMethodId: string,
}) => {
  const {
    clientSecret,
    paymentMethodId,
  } = args;
  const Stripe = (window as any)?.Stripe;
  if (!Stripe) throw new Error('Could not initiate a subscription. Please reach out to us through the chat.');
  const s = await Stripe(process.env.NEXT_PUBLIC_STRIPE_CLIENT_ID as string);
  if (!s) {
    throw new Error('Could not initial Stripe client');
  }
  return s.confirmCardPayment(clientSecret, {
    payment_method: paymentMethodId,
  });
};

export const useBilling = () => {
  const queryClient = useQueryClient();
  const me: any = useMemo(() => queryClient.getQueryData('me'), [queryClient]);
  const org: any = useMemo(() => queryClient.getQueryData('org'), [queryClient]);
  const planDetails = org?.subscription as ({
    status: SubscriptionStatus,
    trialEndDate: string
  });
  const { data: invoices, isLoading: loadingInvoices, isFetching: fetchingInvoices } = useQuery('invoices', getInvoicesAPI, {
    enabled: me?.isAdmin === true,
  });
  const { data: billingDetails, isLoading: loadingBillingDetails } = useQuery('billingDetails', getBillingDetailsAPI, {
    enabled: me?.isAdmin === true,
  });
  const { mutateAsync: startSubscription } = useMutation(startSubscriptionAPI);
  const { mutateAsync: downloadInvoice } = useMutation(downloadInvoiceAPI);
  const { mutateAsync: cancelSubscription } = useMutation(cancelSubscriptionAPI, {
    onSuccess: () => {
      queryClient.invalidateQueries('org');
      queryClient.invalidateQueries('billingDetails');
    },
  });
  const { mutateAsync: updateBillingEmail } = useMutation(updateBillingEmailAPI);
  const { mutateAsync: openCustomerPortal } = useMutation(openCustomerPortalAPI);

  const refetchSubscription = () => {
    queryClient.refetchQueries('org');
  };

  return {
    loadingInvoices,
    fetchingInvoices,
    loadingBillingDetails,
    invoices: invoices || [],
    planDetails,
    billingDetails,
    startSubscription,
    cancelSubscription,
    downloadInvoice,
    updateBillingEmail,
    openCustomerPortal,
    refetchSubscription,
    confirmPayment,
  };
};
